import React, { useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Download, FileText, Loader2, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CompositeImageGenerator = ({ 
  saintName, 
  archangelName,
  prayer,
  feastDay,
  introduction,
  epoch,
  location,
  origin,
  lifeStory,
  symbolism,
  saintImage: existingSaintImage,
  archangelImage: existingArchangelImage
}) => {
  const { language } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [saintImgData, setSaintImgData] = useState(null);
  const [archangelImgData, setArchangelImgData] = useState(null);

  // Helper to poll for task completion
  const pollTaskStatus = async (taskId, maxAttempts = 60) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      try {
        const response = await axios.get(`${API}/task-status/${taskId}`);
        if (response.data.status === 'completed' && response.data.result) {
          return response.data.result;
        } else if (response.data.status === 'failed') {
          throw new Error(response.data.error || 'Generation failed');
        }
      } catch (err) {
        if (err.response?.status === 404) throw new Error('Task not found');
      }
    }
    throw new Error('Timeout');
  };

  const generatePDF = async () => {
    if (!saintName || !archangelName || !prayer) return;
    
    setIsGenerating(true);
    setPdfReady(false);
    
    try {
      toast.info(
        language === 'fr' 
          ? 'Génération des images...' 
          : 'Generating images...'
      );

      // Use existing images if available, otherwise generate new ones
      let saintImageData = existingSaintImage;
      let archangelImageData = existingArchangelImage;

      // Generate saint image if not available
      if (!saintImageData) {
        const saintTask = await axios.post(`${API}/generate-image-async`, {
          saint_name: saintName,
          image_type: 'saint',
          language: language
        });
        const saintResult = await pollTaskStatus(saintTask.data.task_id);
        saintImageData = `data:image/png;base64,${saintResult.image_base64}`;
      }

      toast.info(
        language === 'fr' 
          ? 'Première image terminée...' 
          : 'First image done...'
      );

      // Generate archangel image if not available
      if (!archangelImageData) {
        const archangelTask = await axios.post(`${API}/generate-image-async`, {
          saint_name: archangelName,
          image_type: 'archangel',
          language: language
        });
        const archangelResult = await pollTaskStatus(archangelTask.data.task_id);
        archangelImageData = `data:image/png;base64,${archangelResult.image_base64}`;
      }

      setSaintImgData(saintImageData);
      setArchangelImgData(archangelImageData);

      toast.info(
        language === 'fr' 
          ? 'Création du PDF...' 
          : 'Creating PDF...'
      );

      // Create the PDF
      await createPDF(saintImageData, archangelImageData);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de la génération' 
          : 'Error generating PDF'
      );
      setIsGenerating(false);
    }
  };

  const createPDF = async (saintImageData, archangelImageData) => {
    // Debug: log all content
    console.log('Creating PDF with:', {
      saintName, archangelName, prayer: prayer?.substring(0, 50),
      epoch, location, origin, 
      lifeStory: lifeStory?.substring(0, 50),
      symbolism, introduction: introduction?.substring(0, 50)
    });

    // Create PDF in Letter format (8.5 x 11 inches), portrait
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter'
    });

    const pageWidth = 8.5;
    const pageHeight = 11;
    const margin = 0.5;
    const contentWidth = pageWidth - (margin * 2);
    
    let y = margin;

    // === TITLE ===
    pdf.setFont('times', 'bold');
    pdf.setFontSize(24);
    pdf.setTextColor(139, 69, 19); // Brown color
    const title = language === 'fr' ? `Prière à ${saintName}` : `Prayer to ${saintName}`;
    pdf.text(title, pageWidth / 2, y + 0.3, { align: 'center' });
    
    y += 0.5;

    // Decorative line
    pdf.setDrawColor(201, 162, 39); // Gold
    pdf.setLineWidth(0.02);
    pdf.line(margin + 1, y, pageWidth - margin - 1, y);
    
    y += 0.3;

    // === IMAGES SIDE BY SIDE ===
    const imgWidth = 2.5;
    const imgHeight = 2.5;
    const imgGap = 0.5;
    const totalImgWidth = (imgWidth * 2) + imgGap;
    const imgStartX = (pageWidth - totalImgWidth) / 2;

    try {
      // Add saint image
      pdf.addImage(saintImageData, 'PNG', imgStartX, y, imgWidth, imgHeight);
      
      // Add archangel image
      pdf.addImage(archangelImageData, 'PNG', imgStartX + imgWidth + imgGap, y, imgWidth, imgHeight);
    } catch (err) {
      console.error('Error adding images to PDF:', err);
    }

    y += imgHeight + 0.15;

    // Image labels
    pdf.setFont('times', 'italic');
    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);
    pdf.text(saintName, imgStartX + (imgWidth / 2), y, { align: 'center' });
    pdf.text(archangelName, imgStartX + imgWidth + imgGap + (imgWidth / 2), y, { align: 'center' });

    y += 0.35;

    // === WHO WERE THEY? ===
    if (epoch || location || origin) {
      pdf.setFont('times', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(139, 69, 19);
      pdf.text(language === 'fr' ? 'Qui était-il/elle ?' : 'Who were they?', pageWidth / 2, y, { align: 'center' });
      
      y += 0.25;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      
      // Each info with text wrapping
      if (epoch) {
        const epochText = `${language === 'fr' ? 'Époque' : 'Era'}: ${epoch}`;
        const epochLines = pdf.splitTextToSize(epochText, contentWidth - 1);
        epochLines.forEach(line => {
          pdf.text(line, pageWidth / 2, y, { align: 'center' });
          y += 0.16;
        });
      }
      if (location) {
        const locText = `${language === 'fr' ? 'Lieu' : 'Location'}: ${location}`;
        const locLines = pdf.splitTextToSize(locText, contentWidth - 1);
        locLines.forEach(line => {
          pdf.text(line, pageWidth / 2, y, { align: 'center' });
          y += 0.16;
        });
      }
      if (origin) {
        const originText = `${language === 'fr' ? 'Origine' : 'Origin'}: ${origin}`;
        const originLines = pdf.splitTextToSize(originText, contentWidth - 1);
        originLines.forEach(line => {
          pdf.text(line, pageWidth / 2, y, { align: 'center' });
          y += 0.16;
        });
      }
      y += 0.1;
    }

    // === INTRODUCTION ===
    if (introduction) {
      pdf.setFont('times', 'italic');
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      
      const introLines = pdf.splitTextToSize(introduction, contentWidth - 1.5);
      introLines.forEach(line => {
        pdf.text(line, pageWidth / 2, y, { align: 'center' });
        y += 0.16;
      });
      y += 0.15;
    }

    // === LIFE STORY ===
    if (lifeStory) {
      pdf.setFont('times', 'bold');
      pdf.setFontSize(13);
      pdf.setTextColor(139, 69, 19);
      pdf.text(language === 'fr' ? 'Sa vie' : 'Their Life', pageWidth / 2, y, { align: 'center' });
      
      y += 0.22;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      
      const storyLines = pdf.splitTextToSize(lifeStory, contentWidth - 1.5);
      storyLines.forEach(line => {
        pdf.text(line, pageWidth / 2, y, { align: 'center' });
        y += 0.16;
      });
      y += 0.15;
    }

    // === VIRTUES ===
    if (symbolism && symbolism.length > 0) {
      pdf.setFont('times', 'bold');
      pdf.setFontSize(13);
      pdf.setTextColor(139, 69, 19);
      pdf.text(language === 'fr' ? 'Vertus' : 'Virtues', pageWidth / 2, y, { align: 'center' });
      
      y += 0.22;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      const virtuesText = symbolism.join('  •  ');
      const virtuesLines = pdf.splitTextToSize(virtuesText, contentWidth - 1.5);
      virtuesLines.forEach(line => {
        pdf.text(line, pageWidth / 2, y, { align: 'center' });
        y += 0.16;
      });
      y += 0.15;
    }

    // === FEAST DAY ===
    pdf.setFont('times', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(139, 69, 19);
    pdf.text(`${language === 'fr' ? 'Fête liturgique' : 'Feast day'}: ${feastDay}`, pageWidth / 2, y, { align: 'center' });
    
    y += 0.3;


    // Separator line
    pdf.setDrawColor(201, 162, 39);
    pdf.setLineWidth(0.01);
    pdf.line(margin + 0.5, y, pageWidth - margin - 0.5, y);
    
    y += 0.25;

    // === PRAYER SECTION ===
    pdf.setFont('times', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(139, 69, 19);
    pdf.text(language === 'fr' ? 'Prière' : 'Prayer', pageWidth / 2, y, { align: 'center' });
    
    y += 0.25;

    // Prayer text
    pdf.setFont('times', 'italic');
    pdf.setFontSize(11);
    pdf.setTextColor(40, 40, 40);
    
    const prayerLines = pdf.splitTextToSize(prayer, contentWidth - 1.5);
    prayerLines.forEach(line => {
      pdf.text(line, pageWidth / 2, y, { align: 'center' });
      y += 0.18;
    });

    // === FOOTER ===
    // Line separator
    pdf.setDrawColor(201, 162, 39);
    pdf.setLineWidth(0.01);
    pdf.line(margin, pageHeight - 0.8, pageWidth - margin, pageHeight - 0.8);

    // Website
    pdf.setFont('times', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(139, 69, 19);
    pdf.text('www.monsaintpatron.org', pageWidth / 2, pageHeight - 0.55, { align: 'center' });

    // Tagline
    pdf.setFont('times', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    const tagline = language === 'fr' 
      ? 'Découvrez votre saint patron et recevez une prière personnalisée'
      : 'Discover your patron saint and receive a personalized prayer';
    pdf.text(tagline, pageWidth / 2, pageHeight - 0.35, { align: 'center' });

    // Generate blob
    const blob = pdf.output('blob');
    setPdfBlob(blob);
    setPdfReady(true);
    setIsGenerating(false);

    toast.success(
      language === 'fr' 
        ? 'PDF prêt à télécharger !' 
        : 'PDF ready to download!'
    );
  };

  const downloadPDF = () => {
    if (!pdfBlob) return;
    
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    const fileName = saintName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    link.download = `priere_${fileName}.pdf`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const previewPDF = () => {
    if (!pdfBlob) return;
    
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
  };

  const regeneratePDF = () => {
    setSaintImgData(null);
    setArchangelImgData(null);
    setPdfBlob(null);
    setPdfReady(false);
    generatePDF();
  };

  if (!saintName || !archangelName || !prayer) {
    return null;
  }

  return (
    <Card className="card-divine mt-8 animate-fade-in-up" data-testid="composite-image-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <FileText className="w-5 h-5 text-gold" />
          {language === 'fr' ? 'Feuille de Prière (PDF)' : 'Prayer Sheet (PDF)'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center">
          {!pdfReady && !isGenerating && (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                {language === 'fr' 
                  ? 'Créez une feuille de prière PDF format lettre (8½ x 11) incluant :'
                  : 'Create a letter-size PDF prayer sheet (8½ x 11) including:'}
              </p>
              <ul className="text-sm text-muted-foreground mb-6 space-y-1 text-left max-w-md mx-auto">
                <li>• {language === 'fr' ? 'Portrait du saint et de l\'archange' : 'Saint and archangel portraits'}</li>
                <li>• {language === 'fr' ? 'Histoire et informations complètes' : 'Complete history and information'}</li>
                <li>• {language === 'fr' ? 'Prière personnalisée' : 'Personalized prayer'}</li>
                <li>• {language === 'fr' ? 'Format PDF haute qualité, facile à imprimer' : 'High quality PDF, easy to print'}</li>
              </ul>
              <Button 
                variant="sacred" 
                size="lg" 
                onClick={generatePDF} 
                className="gap-2"
                data-testid="generate-pdf-btn"
              >
                <FileText className="w-5 h-5" />
                {language === 'fr' ? 'Créer le PDF' : 'Create PDF'}
              </Button>
            </div>
          )}
          
          {isGenerating && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">
                {language === 'fr' 
                  ? 'Création de votre feuille de prière...' 
                  : 'Creating your prayer sheet...'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'fr' 
                  ? '(Génération des images, cela peut prendre 1-2 minutes)' 
                  : '(Generating images, this may take 1-2 minutes)'}
              </p>
            </div>
          )}
          
          {pdfReady && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center mb-6">
                <FileText className="w-16 h-16 text-green-600 mx-auto mb-3" />
                <p className="text-green-800 font-medium text-lg">
                  {language === 'fr' ? 'Votre PDF est prêt !' : 'Your PDF is ready!'}
                </p>
                <p className="text-green-600 text-sm mt-1">
                  {language === 'fr' ? 'Format lettre 8½ x 11, portrait' : 'Letter size 8½ x 11, portrait'}
                </p>
              </div>
              
              <div className="flex gap-4 flex-wrap justify-center">
                <Button 
                  variant="sacred" 
                  size="lg" 
                  onClick={downloadPDF} 
                  className="gap-2"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-5 h-5" />
                  {language === 'fr' ? 'Télécharger le PDF' : 'Download PDF'}
                </Button>
                <Button 
                  variant="divine" 
                  size="lg" 
                  onClick={previewPDF} 
                  className="gap-2"
                >
                  <FileText className="w-5 h-5" />
                  {language === 'fr' ? 'Aperçu' : 'Preview'}
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={regeneratePDF} 
                  className="gap-2"
                  data-testid="regenerate-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  {language === 'fr' ? 'Régénérer' : 'Regenerate'}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompositeImageGenerator;
